import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Facebook, Linkedin, Target, CheckCircle, XCircle, ExternalLink, 
  RefreshCw, Save, Trash2, AlertTriangle, Settings, Link2, Unlink,
  TrendingUp, DollarSign, Users, Eye, MousePointer, ArrowLeftRight
} from 'lucide-react';
import LeadFormMappingDialog from '@/components/LeadFormMappingDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform configurations
const PLATFORM_CONFIG = {
  meta: {
    name: 'Meta (Facebook/Instagram)',
    icon: Facebook,
    color: 'bg-blue-600',
    description: 'Connect Facebook & Instagram Ads to track campaigns and capture leads',
    requiredFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', hint: 'Long-lived system user token' },
      { key: 'ad_account_id', label: 'Ad Account ID', type: 'text', hint: 'Format: act_XXXXXXXXXX' },
      { key: 'page_id', label: 'Page ID', type: 'text', hint: 'Facebook Page ID (optional)' }
    ],
    oauthSupported: true,
    docsUrl: 'https://developers.facebook.com/apps/'
  },
  linkedin: {
    name: 'LinkedIn Ads',
    icon: Linkedin,
    color: 'bg-blue-700',
    description: 'Connect LinkedIn Campaign Manager to sync Lead Gen Forms',
    requiredFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', hint: 'OAuth access token' },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', hint: 'For token renewal' },
      { key: 'ad_account_id', label: 'Ad Account ID', type: 'text', hint: 'Campaign Manager account URN' },
      { key: 'organization_id', label: 'Organization ID', type: 'text', hint: 'Company Page URN' }
    ],
    oauthSupported: true,
    docsUrl: 'https://www.linkedin.com/developers/apps'
  },
  google_ads: {
    name: 'Google Ads',
    icon: Target,
    color: 'bg-yellow-500',
    description: 'Connect Google Ads to track campaigns and lead form submissions',
    requiredFields: [
      { key: 'developer_token', label: 'Developer Token', type: 'password', hint: 'From Google Ads API Center' },
      { key: 'customer_id', label: 'Customer ID', type: 'text', hint: 'Format: XXX-XXX-XXXX' },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', hint: 'OAuth refresh token' }
    ],
    oauthSupported: true,
    docsUrl: 'https://ads.google.com/aw/apicenter'
  }
};

const SocialAdsSettings = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({});
  
  // Dialog state
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Lead Form Mapping dialog state
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState(false);
  const [mappingPlatform, setMappingPlatform] = useState(null);
  
  // Open field mapping dialog
  const openFieldMappingDialog = (platformKey) => {
    setMappingPlatform(platformKey);
    setIsMappingDialogOpen(true);
  };
  
  // Fetch platform statuses
  const fetchPlatforms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/social-integrations?token=${token}`);
      if (response.data.success) {
        setPlatforms(response.data.platforms);
      }
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
      toast.error('Failed to load social integrations');
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  // Fetch metrics summary
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/marketing/ad-metrics/summary?token=${token}&days=30`);
      if (response.data.success) {
        setMetrics(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  }, [token]);
  
  useEffect(() => {
    fetchPlatforms();
    fetchMetrics();
    
    // Check for OAuth callback messages
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success) {
      toast.success(`${success.replace('_', ' ')} successfully!`);
    }
    if (error) {
      toast.error(`Connection failed: ${error.replace(/_/g, ' ')}`);
    }
  }, [fetchPlatforms, fetchMetrics, searchParams]);
  
  // Connect via OAuth
  const handleOAuthConnect = (platform) => {
    window.location.href = `${API}/auth/${platform === 'google_ads' ? 'google-ads' : platform}/connect?token=${token}`;
  };
  
  // Open manual config dialog
  const openConfigDialog = (platform) => {
    setSelectedPlatform(platform);
    setCredentials({});
    setIsConfigDialogOpen(true);
  };
  
  // Save credentials
  const handleSaveCredentials = async () => {
    if (!selectedPlatform) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/settings/social-integrations/${selectedPlatform}?token=${token}`,
        { credentials }
      );
      
      if (response.data.success) {
        toast.success('Credentials saved successfully');
        setIsConfigDialogOpen(false);
        fetchPlatforms();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };
  
  // Test connection
  const handleTestConnection = async (platform) => {
    setTesting(true);
    try {
      const response = await axios.post(
        `${API}/settings/social-integrations/${platform}/test?token=${token}`
      );
      
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };
  
  // Disconnect platform
  const handleDisconnect = async (platform) => {
    if (!confirm(`Are you sure you want to disconnect ${PLATFORM_CONFIG[platform].name}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/settings/social-integrations/${platform}?token=${token}`);
      toast.success('Platform disconnected');
      fetchPlatforms();
    } catch (error) {
      toast.error('Failed to disconnect platform');
    }
  };
  
  // Trigger manual sync
  const handleSync = async (platform) => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/settings/social-integrations/${platform}/sync?token=${token}`
      );
      
      if (response.data.success) {
        toast.success(`Sync completed: ${response.data.campaigns_synced || response.data.leads_created || 0} items`);
        fetchMetrics();
      } else {
        toast.error(response.data.error || 'Sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };
  
  // Get platform status
  const getPlatformStatus = (platformId) => {
    const platform = platforms.find(p => p.platform === platformId);
    return platform?.connection_status || 'disconnected';
  };
  
  // Get platform info
  const getPlatformInfo = (platformId) => {
    return platforms.find(p => p.platform === platformId) || {};
  };
  
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Connected</Badge>;
      case 'token_expired':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle size={12} className="mr-1" /> Token Expired</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600"><Unlink size={12} className="mr-1" /> Not Connected</Badge>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="social-ads-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Social Ads Integrations</h1>
          <p className="text-sm text-slate-500 mt-1">
            Connect your ad platforms to track campaigns and capture leads automatically
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchPlatforms(); fetchMetrics(); }} className="gap-2">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>
      
      {/* Metrics Summary */}
      {Object.keys(metrics).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp size={18} /> Last 30 Days Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(metrics).map(([platform, data]) => (
                <div key={platform} className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {PLATFORM_CONFIG[platform]?.icon && (
                      <div className={`w-6 h-6 rounded flex items-center justify-center text-white ${PLATFORM_CONFIG[platform].color}`}>
                        {(() => { const Icon = PLATFORM_CONFIG[platform].icon; return <Icon size={14} />; })()}
                      </div>
                    )}
                    <span className="text-xs font-medium text-slate-600">{PLATFORM_CONFIG[platform]?.name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500">Leads</p>
                      <p className="font-semibold text-slate-900">{data.leads || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">CPL</p>
                      <p className="font-semibold text-slate-900">AED {data.cost_per_lead?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Spend</p>
                      <p className="font-semibold text-slate-900">AED {data.spend?.toFixed(0) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Clicks</p>
                      <p className="font-semibold text-slate-900">{data.clicks || 0}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(PLATFORM_CONFIG).map(([platformId, config]) => {
          const status = getPlatformStatus(platformId);
          const info = getPlatformInfo(platformId);
          const Icon = config.icon;
          
          return (
            <Card key={platformId} className={`relative overflow-hidden ${status === 'connected' ? 'border-emerald-200' : ''}`}>
              <div className={`absolute top-0 left-0 w-full h-1 ${config.color}`} />
              
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white ${config.color}`}>
                      <Icon size={24} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{config.name}</CardTitle>
                      {renderStatusBadge(status)}
                    </div>
                  </div>
                </div>
                <CardDescription className="mt-2">{config.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Status Info */}
                {status === 'connected' && (
                  <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                    {info.last_sync_at && (
                      <p className="text-slate-600">
                        Last sync: {new Date(info.last_sync_at).toLocaleString()}
                      </p>
                    )}
                    {info.last_lead_received_at && (
                      <p className="text-slate-600">
                        Last lead: {new Date(info.last_lead_received_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Error Message */}
                {info.last_error_message && (
                  <Alert variant="destructive" className="py-2">
                    <AlertDescription className="text-xs">{info.last_error_message}</AlertDescription>
                  </Alert>
                )}
                
                {/* Token Expiry Warning */}
                {status === 'token_expired' && (
                  <Alert className="py-2 bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-xs text-amber-700">
                      Your token has expired. Please reconnect to continue syncing.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {status === 'disconnected' ? (
                    <>
                      {config.oauthSupported && (
                        <Button 
                          onClick={() => handleOAuthConnect(platformId)}
                          className={`flex-1 gap-2 ${config.color} hover:opacity-90`}
                          data-testid={`connect-${platformId}-btn`}
                        >
                          <Link2 size={14} /> Connect with OAuth
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        onClick={() => openConfigDialog(platformId)}
                        className="gap-2"
                        data-testid={`manual-config-${platformId}-btn`}
                      >
                        <Settings size={14} /> Manual Setup
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => openFieldMappingDialog(platformId)}
                        className="gap-2"
                        title="Configure field mappings before connecting"
                      >
                        <ArrowLeftRight size={14} /> Field Mapping
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleTestConnection(platformId)}
                        disabled={testing}
                        className="gap-1"
                      >
                        {testing ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                        Test
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSync(platformId)}
                        disabled={syncing}
                        className="gap-1"
                      >
                        {syncing ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        Sync
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openConfigDialog(platformId)}
                        className="gap-1"
                      >
                        <Settings size={12} /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openFieldMappingDialog(platformId)}
                        className="gap-1"
                      >
                        <ArrowLeftRight size={12} /> Field Mapping
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDisconnect(platformId)}
                        className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Disconnect
                      </Button>
                    </>
                  )}
                </div>
                
                {/* Docs Link */}
                <a 
                  href={config.docsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink size={12} /> View setup documentation
                </a>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Configuration Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPlatform && (() => {
                const Icon = PLATFORM_CONFIG[selectedPlatform]?.icon;
                return Icon ? <Icon size={20} /> : null;
              })()}
              Configure {selectedPlatform && PLATFORM_CONFIG[selectedPlatform]?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials below. All credentials are encrypted at rest.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedPlatform && PLATFORM_CONFIG[selectedPlatform]?.requiredFields.map(field => (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={field.key}>{field.label}</Label>
                <Input
                  id={field.key}
                  type={field.type}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.hint}
                  data-testid={`input-${field.key}`}
                />
                {field.hint && (
                  <p className="text-xs text-slate-500">{field.hint}</p>
                )}
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCredentials} disabled={saving} className="gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Lead Form Mapping Dialog */}
      <LeadFormMappingDialog
        open={isMappingDialogOpen}
        onOpenChange={setIsMappingDialogOpen}
        platform={mappingPlatform}
      />
    </div>
  );
};

export default SocialAdsSettings;
